import apiClient from '@/core/api/client';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import { CreateWorkflowRequest, WorkflowResponse, WorkflowListResponse, CreateWorkflowStepRequest, WorkflowStepResponse, WorkflowStepListResponse, CreateWorkflowLevelRequest, WorkflowLevelResponse, WorkflowDetailResponse } from '../types';

export const workflowApi = {
    createWorkflow: async (schoolSlug: string, data: CreateWorkflowRequest): Promise<WorkflowResponse> => {
        try {
            const response = await apiClient.post<WorkflowResponse>(
                API_ENDPOINTS.WORKFLOWS_NEW.CREATE(schoolSlug),
                data
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to create workflow',
            };
        }
    },
    getWorkflows: async (schoolSlug: string, filters?: { department_id?: string; page?: number; page_size?: number }): Promise<WorkflowListResponse> => {
        try {
            const response = await apiClient.get<WorkflowListResponse>(
                API_ENDPOINTS.WORKFLOWS_NEW.LIST(schoolSlug),
                { params: filters }
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to load workflows',
            };
        }
    },
    getWorkflowsByDepartment: async (schoolSlug: string, departmentId: string): Promise<{ success: boolean; data?: any[]; error?: string }> => {
        try {
            const response = await apiClient.get<{ success: boolean; data?: any[]; error?: string }>(
                API_ENDPOINTS.WORKFLOWS_NEW.BY_DEPARTMENT(schoolSlug, departmentId)
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to load workflows for department',
            };
        }
    },
    updateWorkflow: async (schoolSlug: string, workflowId: string, data: Partial<CreateWorkflowRequest>): Promise<WorkflowResponse> => {
        try {
            const response = await apiClient.patch<WorkflowResponse>(
                API_ENDPOINTS.WORKFLOWS_NEW.UPDATE(schoolSlug, workflowId),
                data
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to update workflow',
            };
        }
    },
    deleteWorkflow: async (schoolSlug: string, workflowId: string): Promise<WorkflowResponse> => {
        try {
            const response = await apiClient.delete<WorkflowResponse>(
                API_ENDPOINTS.WORKFLOWS_NEW.DELETE(schoolSlug, workflowId)
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to delete workflow',
            };
        }
    },
    createWorkflowStep: async (schoolSlug: string, data: CreateWorkflowStepRequest): Promise<WorkflowStepResponse> => {
        try {
            const response = await apiClient.post<WorkflowStepResponse>(
                API_ENDPOINTS.WORKFLOWS_NEW.STEPS.CREATE(schoolSlug),
                data
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to create workflow step',
            };
        }
    },
    getWorkflowSteps: async (schoolSlug: string, workflowId: string): Promise<WorkflowDetailResponse> => {
        try {
            const response = await apiClient.get<WorkflowDetailResponse>(
                API_ENDPOINTS.WORKFLOWS_NEW.STEPS.LIST(schoolSlug, workflowId)
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to load workflow steps',
            };
        }
    },
    updateWorkflowStep: async (schoolSlug: string, stepId: string, data: { name: string, description: string }): Promise<WorkflowStepResponse> => {
        try {
            const response = await apiClient.patch<WorkflowStepResponse>(
                API_ENDPOINTS.WORKFLOWS_NEW.STEPS.UPDATE(schoolSlug, stepId),
                data
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to update workflow step',
            };
        }
    },
    deleteWorkflowStep: async (schoolSlug: string, stepId: string): Promise<WorkflowStepResponse> => {
        try {
            const response = await apiClient.delete<WorkflowStepResponse>(
                API_ENDPOINTS.WORKFLOWS_NEW.STEPS.DELETE(schoolSlug, stepId)
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to delete workflow step',
            };
        }
    },
    createWorkflowLevel: async (schoolSlug: string, data: CreateWorkflowLevelRequest): Promise<WorkflowLevelResponse> => {
        try {
            const response = await apiClient.post<WorkflowLevelResponse>(
                API_ENDPOINTS.WORKFLOWS_NEW.LEVELS.CREATE(schoolSlug),
                data
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to create workflow level',
            };
        }
    },
    updateWorkflowLevel: async (schoolSlug: string, levelId: string, data: any): Promise<WorkflowLevelResponse> => {
        try {
            const response = await apiClient.patch<WorkflowLevelResponse>(
                API_ENDPOINTS.WORKFLOWS_NEW.LEVELS.UPDATE(schoolSlug, levelId),
                data
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to update workflow level',
            };
        }
    },
    deleteWorkflowLevel: async (schoolSlug: string, levelId: string): Promise<WorkflowLevelResponse> => {
        try {
            const response = await apiClient.delete<WorkflowLevelResponse>(
                API_ENDPOINTS.WORKFLOWS_NEW.LEVELS.DELETE(schoolSlug, levelId)
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to delete workflow level',
            };
        }
    }
};
